import { Link } from '@tanstack/react-router'
import { ArrowLeft, Mail, MessageSquare, MousePointerClick, Eye, Send, XCircle } from 'lucide-react'

import {
  CAMPAIGN_CHANNEL_LABELS,
  CAMPAIGN_STATUS_LABELS,
  CAMPAIGN_STATUS_VARIANT,
  type CampaignStats,
} from '@/types/marketing'
import { useCampaign, useCampaignRecipients } from '@/features/marketing/hooks'
import { RECIPIENT_STATUS_LABELS, RECIPIENT_STATUS_VARIANT } from '@/types/marketing'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

export function CampaignDetail({ campaignId }: { campaignId: string }) {
  const { data: campaign, isLoading } = useCampaign(campaignId)
  const { data: recipients = [], isLoading: recipientsLoading } = useCampaignRecipients(campaignId)

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }
  if (!campaign) {
    return <p className="text-sm text-muted-foreground">Campaign not found.</p>
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <Button variant="ghost" size="sm" asChild className="mb-1 -ml-2 text-muted-foreground">
            <Link to="/marketing">
              <ArrowLeft className="mr-1 h-4 w-4" /> Marketing
            </Link>
          </Button>
          <h1 className="text-3xl font-bold tracking-tight">{campaign.name}</h1>
          <div className="mt-1 flex items-center gap-2 text-sm text-muted-foreground">
            <Badge variant={CAMPAIGN_STATUS_VARIANT[campaign.status]}>
              {CAMPAIGN_STATUS_LABELS[campaign.status]}
            </Badge>
            <Badge variant="outline" className="gap-1">
              {campaign.channel === 'email' ? (
                <Mail className="h-3 w-3" />
              ) : (
                <MessageSquare className="h-3 w-3" />
              )}
              {CAMPAIGN_CHANNEL_LABELS[campaign.channel]}
            </Badge>
            {campaign.sent_at && <span>sent {new Date(campaign.sent_at).toLocaleString()}</span>}
          </div>
        </div>
      </div>

      <StatsCards stats={campaign.stats} recipientsLoading={recipientsLoading} />

      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-1">
          <CardContent className="space-y-3 pt-6">
            <h3 className="text-sm font-semibold">Message</h3>
            {campaign.channel === 'email' && campaign.subject && (
              <p className="text-sm font-medium">{campaign.subject}</p>
            )}
            <pre className="whitespace-pre-wrap break-words font-sans text-sm text-muted-foreground">
              {campaign.body}
            </pre>
            <p className="text-xs text-muted-foreground">
              Content is snapshotted at send time; later template edits don't rewrite this.
            </p>
          </CardContent>
        </Card>

        <div className="lg:col-span-2">
          <h3 className="mb-2 text-sm font-semibold">Send log</h3>
          {recipientsLoading ? (
            <Skeleton className="h-48 w-full" />
          ) : recipients.length === 0 ? (
            <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
              No recipients yet — the send log fills in when the campaign sends.
            </div>
          ) : (
            <div className="rounded-lg border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Customer</TableHead>
                    <TableHead>Address</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Detail</TableHead>
                    <TableHead>Sent</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recipients.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell className="font-medium">{r.customer_name || '—'}</TableCell>
                      <TableCell className="text-sm">{r.address || '—'}</TableCell>
                      <TableCell>
                        <Badge variant={RECIPIENT_STATUS_VARIANT[r.status]}>
                          {RECIPIENT_STATUS_LABELS[r.status]}
                        </Badge>
                      </TableCell>
                      <TableCell className="max-w-48">
                        <span className="text-xs text-muted-foreground">
                          {r.error || (r.opened_at ? 'opened' : '')}
                        </span>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {r.sent_at ? new Date(r.sent_at).toLocaleString() : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function StatsCards({
  stats,
  recipientsLoading,
}: {
  stats: CampaignStats
  recipientsLoading: boolean
}) {
  const cards = [
    { label: 'Recipients', value: stats.recipients, icon: Send },
    { label: 'Delivered', value: stats.sent, icon: Mail },
    { label: 'Failed', value: stats.failed, icon: XCircle },
    { label: 'Skipped', value: stats.skipped, icon: Eye },
    { label: 'Opened', value: stats.opened, icon: Eye },
    { label: 'Clicked', value: stats.clicked, icon: MousePointerClick },
  ]
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
      {cards.map(({ label, value, icon: Icon }) => (
        <Card key={label}>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Icon className="h-3.5 w-3.5" /> {label}
            </div>
            <div className="mt-1 text-2xl font-bold">
              {recipientsLoading ? <Skeleton className="h-8 w-10" /> : value}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  )
}
